// stores/useEmailTemplateStore.ts

import { defineStore } from 'pinia';
import { ref, watch, computed } from 'vue';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { IPaginationDTO, PaginationDTO } from '@/dtos';
import { EmailTemplateDTO, IEmailTemplateDTO } from '@/dtos/email.template.dto';
import {
  getAllEmailTemplatesAPI,
  getEmailTemplateByIdAPI,
  updateEmailTemplateAPI,
} from '@/services/admin';
import { useUpdateStatusStore } from '../updateStatus';

export const useEmailTemplateStore = defineStore('emailTemplate', () => {
  const queryClient = useQueryClient();

  const emailTemplateId = ref<string | undefined>(undefined);
  const pagination = ref<IPaginationDTO>(new PaginationDTO());
  const statusStore = useUpdateStatusStore();
  const paginationQueryKey = computed(() => ['email-templates', pagination.value]);
  const queryByIDKey = computed(() => ['email-template', emailTemplateId]);
  const fetchEmailTemplates = async (): Promise<IEmailTemplateDTO[]> => {
    const ret = await getAllEmailTemplatesAPI(pagination.value);
    const emails = await Promise.all(ret.data.map((email) => new EmailTemplateDTO(email)));
    return emails;
  };
  const getEmailTemplateById = async (): Promise<IEmailTemplateDTO> => {
    if (!emailTemplateId.value) {
      return new EmailTemplateDTO();
    }
    const emailTemplate = await getEmailTemplateByIdAPI(emailTemplateId.value);
    return new EmailTemplateDTO(emailTemplate);
  };

  const emailTemplatePaginationQuery = useQuery({
    queryKey: paginationQueryKey,
    queryFn: fetchEmailTemplates,
  });

  const getEmailTemplateByIdQuery = useQuery({
    queryKey: queryByIDKey,
    queryFn: getEmailTemplateById,
  });

  const update = useMutation({
    mutationFn: async (template: IEmailTemplateDTO) => {
      if (!emailTemplateId.value) return;
      return await updateEmailTemplateAPI(emailTemplateId.value, template);
    },
    onSuccess: (data, variables) => {
      statusStore.setSuccessStatus(`email-template-${variables.id}`, 'success:update');
      if (data && data.id != '') {
        queryClient.setQueryData(['email-template', data.id], data);
      }
    },
    onError: (err: string, variables) => {
      statusStore.setErrorStatus(`email-template-${variables.id}`, err);
    },
  });

  watch(pagination, () => {
    emailTemplatePaginationQuery.refetch();
  });
  watch(emailTemplateId, () => {
    getEmailTemplateByIdQuery.refetch();
  });

  const refetchTemplates = () => {
    emailTemplatePaginationQuery.refetch();
  };

  const setPagination = (newPagination: IPaginationDTO) => {
    pagination.value = newPagination;
  };

  return {
    emailTemplateId,
    pagination,
    emailTemplatePaginationQuery,
    update,
    getEmailTemplateByIdQuery,
    refetchTemplates,
    setPagination,
  };
});
